// src/components/common/EmojiRenderer.tsx
import React, { ReactNode } from 'react';
import { unicodeToCustomEmoji, emojiRegex } from '../../utils/emojiMapping';

interface EmojiImageProps {
  src: string;
  alt: string;
  fallback: string;
  size?: number;
  isInInput?: boolean;
}

const EmojiImage: React.FC<EmojiImageProps> = ({ src, alt, fallback, size = 20, isInInput = false }) => {
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return <span style={{ fontSize: isInInput ? '16px' : 'inherit' }}>{fallback}</span>;
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        display: 'inline-block',
        verticalAlign: 'middle',
        margin: '0 1px',
        objectFit: 'contain',
      }}
      onError={() => setHasError(true)}
      onLoad={() => setHasError(false)}
    />
  );
};

interface EmojiRendererProps {
  text: string;
  isInInput?: boolean;
  emojiSize?: number;
}

export const EmojiRenderer: React.FC<EmojiRendererProps> = ({ text, isInInput = false, emojiSize }) => {
  const renderTextWithEmojis = (inputText: string): ReactNode[] => {
    if (!inputText) return [];

    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let match;

    emojiRegex.lastIndex = 0;

    while ((match = emojiRegex.exec(inputText)) !== null) {
      if (match.index > lastIndex) {
        const textPart = inputText.slice(lastIndex, match.index);
        if (textPart) {
          parts.push(textPart);
        }
      }

      const unicodeEmoji = match[0];
      const customEmoji = unicodeToCustomEmoji.get(unicodeEmoji);

      if (customEmoji) {
        parts.push(
          <EmojiImage
            key={`emoji-${match.index}-${customEmoji.id}`}
            src={customEmoji.src}
            alt={customEmoji.description}
            fallback={unicodeEmoji}
            size={emojiSize || (isInInput ? 22 : 26)}
            isInInput={isInInput}
          />,
        );
      } else {
        parts.push(
          <span key={`fallback-${match.index}`} style={{ fontSize: isInInput ? '16px' : 'inherit' }}>
            {unicodeEmoji}
          </span>,
        );
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < inputText.length) {
      const remainingText = inputText.slice(lastIndex);
      if (remainingText) {
        parts.push(remainingText);
      }
    }

    return parts.length > 0 ? parts : [inputText];
  };

  const renderedContent = renderTextWithEmojis(text);

  return (
    <>
      {renderedContent.map((part, index) => (
        <React.Fragment key={`part-${index}`}>{part}</React.Fragment>
      ))}
    </>
  );
};
